import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import { POLICY_GROUPS, CURATED_POLICY_IDS } from '../policyGroups'

const RESERVED_POLICY_ID_SET = new Set(['allow', 'forbidden', 'smart_endpoint'])

const CONFIG_LIMITS = {
  maxReadRequests: { min: 0, max: 1000 },
  maxWriteRequests: { min: 0, max: 1000 },
  timeoutMs: { min: 1000, max: 29000 }
}

function clampConfigValue (field, value, fallback) {
  const { min, max } = CONFIG_LIMITS[field]
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return fallback
  return Math.min(max, Math.max(min, numeric))
}

const useStyles = makeStyles(() => ({
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 199
  },
  popover: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    right: 0,
    maxHeight: 'calc(100vh - 200px)',
    overflowY: 'auto',
    backgroundColor: 'white',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    boxShadow: 'var(--shadow-md)',
    zIndex: 200,
    padding: '14px',
    display: 'flex',
    flexDirection: 'row',
    gap: '0'
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  columnLeft: {
    width: '260px',
    paddingRight: '14px'
  },
  columnRight: {
    width: '180px',
    paddingLeft: '14px',
    borderLeft: '1px solid var(--color-neutral-200)'
  },
  title: {
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: 'var(--color-neutral-400)'
  },
  field: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px'
  },
  label: {
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--color-neutral-700)'
  },
  input: {
    width: '70px',
    padding: '4px 8px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '12px',
    textAlign: 'right',
    fontFamily: 'var(--font-mono, monospace)',
    outline: 'none',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    }
  },
  chipGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px'
  },
  chip: {
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: 500,
    cursor: 'pointer',
    border: '1px solid var(--color-neutral-300)',
    backgroundColor: 'white',
    color: 'var(--color-neutral-600)',
    transition: 'all 0.1s ease',
    lineHeight: '18px',
    '&:hover': {
      borderColor: 'var(--color-primary-400)',
      color: 'var(--color-primary-600)'
    }
  },
  chipActive: {
    backgroundColor: 'var(--color-primary-500)',
    borderColor: 'var(--color-primary-600)',
    color: 'white',
    fontWeight: 600,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.15)',
    '&:hover': {
      backgroundColor: 'var(--color-primary-600)',
      borderColor: 'var(--color-primary-700)',
      color: 'white'
    }
  },
  customTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 6px 2px 8px',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: 500,
    backgroundColor: 'var(--color-neutral-100)',
    color: 'var(--color-neutral-700)',
    border: '1px solid var(--color-neutral-300)',
    lineHeight: '18px',
    '& button': {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: 'var(--color-neutral-400)',
      fontWeight: 600,
      fontSize: '12px',
      padding: 0,
      lineHeight: 1,
      '&:hover': {
        color: 'var(--color-danger-500)'
      }
    }
  },
  groupLabel: {
    fontSize: '10px',
    fontWeight: 600,
    color: 'var(--color-neutral-400)',
    marginBottom: '3px'
  },
  addBtn: {
    background: 'none',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    cursor: 'pointer',
    padding: '2px 8px',
    fontSize: '11px',
    color: 'var(--color-neutral-600)',
    whiteSpace: 'nowrap',
    '&:disabled': {
      opacity: 0.5,
      cursor: 'default'
    }
  }
}))

export { CONFIG_LIMITS, clampConfigValue }

function ConfigPopover ({ endpointConfig, onConfigChange, onToast, onClose }) {
  const classes = useStyles()
  const [newPolicyInput, setNewPolicyInput] = useState('')

  const handleTogglePolicy = useCallback((policyId) => {
    onConfigChange(prev => {
      const has = prev.allowedPolicies.includes(policyId)
      return {
        ...prev,
        allowedPolicies: has
          ? prev.allowedPolicies.filter(p => p !== policyId)
          : [...prev.allowedPolicies, policyId]
      }
    })
  }, [onConfigChange])

  const handleAddPolicy = useCallback(() => {
    const policy = newPolicyInput.trim()
    if (!policy) return
    if (policy.includes(':')) {
      onToast({
        message: 'Policy IDs cannot contain colons — they conflict with the invocation code format.',
        severity: 'error'
      })
      return
    }
    if (RESERVED_POLICY_ID_SET.has(policy)) {
      onToast({
        message: `Policy '${policy}' is reserved and cannot be assigned to a smart endpoint.`,
        severity: 'error'
      })
      return
    }
    onConfigChange(prev => {
      if (prev.allowedPolicies.includes(policy)) return prev
      return { ...prev, allowedPolicies: [...prev.allowedPolicies, policy] }
    })
    setNewPolicyInput('')
  }, [newPolicyInput, onConfigChange, onToast])

  const handleLimitChange = useCallback((field, raw) => {
    if (!(field in CONFIG_LIMITS)) return
    const val = parseInt(raw, 10)
    if (isNaN(val)) return
    onConfigChange(prev => ({
      ...prev,
      [field]: clampConfigValue(field, val, prev[field])
    }))
  }, [onConfigChange])

  const customPolicies = useMemo(
    () => endpointConfig.allowedPolicies.filter(p => !CURATED_POLICY_IDS.has(p)),
    [endpointConfig.allowedPolicies]
  )

  return (
    <>
      <div className={classes.backdrop} onClick={onClose} />
      <div className={classes.popover}>
        <div className={`${classes.column} ${classes.columnLeft}`}>
          <div className={classes.title}>Access Control</div>
          {POLICY_GROUPS.map(group => (
            <div key={group.label}>
              <div className={classes.groupLabel}>{group.label}</div>
              <div className={classes.chipGroup}>
                {group.policies.map(p => {
                  const active = endpointConfig.allowedPolicies.includes(p.id)
                  return (
                    <button
                      key={p.id}
                      className={`${classes.chip} ${active ? classes.chipActive : ''}`}
                      onClick={() => handleTogglePolicy(p.id)}
                      title={p.id}
                    >
                      {p.label}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
          {customPolicies.length > 0 && (
            <div>
              <div className={classes.groupLabel}>Custom</div>
              <div className={classes.chipGroup}>
                {customPolicies.map(p => (
                  <span key={p} className={classes.customTag}>
                    {p}
                    <button onClick={() => handleTogglePolicy(p)} aria-label={`Remove ${p}`}>×</button>
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className={classes.field} style={{ gap: 4 }}>
            <input
              className={classes.input}
              style={{ flex: 1, width: 'auto', textAlign: 'left' }}
              type='text'
              placeholder='Custom policy ID'
              value={newPolicyInput}
              onChange={(e) => setNewPolicyInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddPolicy() }}
            />
            <button
              className={classes.addBtn}
              onClick={handleAddPolicy}
              disabled={!newPolicyInput.trim()}
            >
              Add
            </button>
          </div>
        </div>
        <div className={`${classes.column} ${classes.columnRight}`}>
          <div className={classes.title}>Limits</div>
          <div className={classes.field}>
            <span className={classes.label}>Max reads</span>
            <input
              className={classes.input}
              type='number'
              min={CONFIG_LIMITS.maxReadRequests.min}
              max={CONFIG_LIMITS.maxReadRequests.max}
              value={endpointConfig.maxReadRequests}
              onChange={(e) => handleLimitChange('maxReadRequests', e.target.value)}
            />
          </div>
          <div className={classes.field}>
            <span className={classes.label}>Max writes</span>
            <input
              className={classes.input}
              type='number'
              min={CONFIG_LIMITS.maxWriteRequests.min}
              max={CONFIG_LIMITS.maxWriteRequests.max}
              value={endpointConfig.maxWriteRequests}
              onChange={(e) => handleLimitChange('maxWriteRequests', e.target.value)}
            />
          </div>
          <div className={classes.field}>
            <span className={classes.label}>Timeout</span>
            <input
              className={classes.input}
              type='number'
              min={CONFIG_LIMITS.timeoutMs.min}
              max={CONFIG_LIMITS.timeoutMs.max}
              step='1000'
              value={endpointConfig.timeoutMs}
              onChange={(e) => handleLimitChange('timeoutMs', e.target.value)}
            />
          </div>
        </div>
      </div>
    </>
  )
}

ConfigPopover.propTypes = {
  endpointConfig: PropTypes.object.isRequired,
  onConfigChange: PropTypes.func.isRequired,
  onToast: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
}

export default ConfigPopover
